import type { CSSProperties, FC, KeyboardEvent, PointerEvent, RefObject } from 'react'
import type { Sticker } from '@/constants/home'

import { useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { hand } from '@/lib/fonts'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'
import { stickers } from '@/constants/home'

const star = (points: number, outer: number, inner: number): string => {
    const steps = points * 2

    return Array.from({ length: steps }, (_, index) => {
        const radius = index % 2 === 0 ? outer : inner
        const angle = (Math.PI * 2 * index) / steps - Math.PI / 2

        return `${(50 + radius * Math.cos(angle)).toFixed(2)}% ${(50 + radius * Math.sin(angle)).toFixed(2)}%`
    }).join(', ')
}

const shapes: Record<Sticker['shape'], { width: string, aspect: string, radius?: string, clip?: string }> = {
    circle: { width: '19%', aspect: '1 / 1', radius: '9999px' },
    pill: { width: '27%', aspect: '2.3 / 1', radius: '9999px' },
    burst: { width: '22%', aspect: '1 / 1', clip: `polygon(${star(14, 50, 41)})` },
    square: { width: '19%', aspect: '1 / 1', radius: '1.5rem' },
    flower: { width: '20%', aspect: '1 / 1', clip: `polygon(${star(12, 50, 45)})` },
    arch: { width: '18%', aspect: '0.85 / 1', radius: '9999px 9999px 1.25rem 1.25rem' }
}

type Position = { x: number, y: number }

interface StickerItemProps {
    sticker: Sticker
    position: Position
    z: number
    onMove: (position: Position) => void
    onLift: () => void
    sheetRef: RefObject<HTMLDivElement | null>
}

const StickerItem: FC<StickerItemProps> = ({ sticker, position, z, onMove, onLift, sheetRef }) => {
    const [dragging, setDragging] = useState(false)
    const offset = useRef({ x: 0, y: 0 })
    const shape = shapes[sticker.shape]

    const clampPosition = (x: number, y: number, element: HTMLElement): Position => {
        const sheet = sheetRef.current

        if (!sheet) return { x, y }

        const maxX = 100 - (element.offsetWidth / sheet.offsetWidth) * 100
        const maxY = 100 - (element.offsetHeight / sheet.offsetHeight) * 100

        return {
            x: Math.min(Math.max(0, x), maxX),
            y: Math.min(Math.max(0, y), maxY)
        }
    }

    const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
        const sheet = sheetRef.current

        if (!sheet || event.button !== 0) return

        const rect = event.currentTarget.getBoundingClientRect()

        offset.current = { x: event.clientX - rect.left, y: event.clientY - rect.top }
        event.currentTarget.setPointerCapture(event.pointerId)
        setDragging(true)
        onLift()
    }

    const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
        const sheet = sheetRef.current

        if (!dragging || !sheet) return

        const rect = sheet.getBoundingClientRect()
        const x = ((event.clientX - rect.left - offset.current.x) / rect.width) * 100
        const y = ((event.clientY - rect.top - offset.current.y) / rect.height) * 100

        onMove(clampPosition(x, y, event.currentTarget))
    }

    const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)

        setDragging(false)
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const step = event.shiftKey ? 8 : 2
        const moves: Record<string, Position> = {
            ArrowLeft: { x: -step, y: 0 },
            ArrowRight: { x: step, y: 0 },
            ArrowUp: { x: 0, y: -step },
            ArrowDown: { x: 0, y: step }
        }
        const move = moves[event.key]

        if (!move) return

        event.preventDefault()
        onLift()
        onMove(clampPosition(position.x + move.x, position.y + move.y, event.currentTarget))
    }

    const shapeStyle: CSSProperties = {
        borderRadius: shape.radius,
        clipPath: shape.clip
    }

    return (
        <div
            role='button'
            tabIndex={0}
            aria-label={`${sticker.word} sticker: ${sticker.line}. Drag it, or use the arrow keys to move it.`}
            data-cursor={dragging ? 'Pin it' : 'Drag'}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onKeyDown={onKeyDown}
            className={cn(
                'group absolute touch-none select-none outline-none transition-[scale,filter] duration-300 ease-out focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-4',
                dragging ? 'scale-110 cursor-grabbing [filter:drop-shadow(0_24px_22px_rgb(30_30_30/0.35))]' : 'cursor-grab [filter:drop-shadow(0_6px_6px_rgb(30_30_30/0.18))] hover:scale-[1.04] hover:[filter:drop-shadow(0_16px_14px_rgb(30_30_30/0.28))]'
            )}
            style={{
                left: `${position.x}%`,
                top: `${position.y}%`,
                width: shape.width,
                aspectRatio: shape.aspect,
                zIndex: z,
                rotate: `${sticker.rotate}deg`
            }}
        >
            <div
                className='h-full w-full bg-white p-[5%]'
                style={shapeStyle}
            >
                <div
                    className='@container relative flex h-full w-full flex-col items-center justify-center overflow-hidden px-[8%] text-center'
                    style={{ ...shapeStyle, backgroundColor: sticker.bg, color: sticker.fg }}
                >
                    <span className='font-heading text-[length:clamp(0.9rem,15cqw,2.6rem)] font-bold leading-[0.9] tracking-[-0.04em]'>
                        {sticker.word}
                    </span>
                    <span
                        aria-hidden='true'
                        className='absolute bottom-0 right-0 size-0 bg-[linear-gradient(135deg,transparent_50%,#ffffff_50%,#ebe6dd_100%)] shadow-[-4px_-4px_8px_rgb(30_30_30/0.15)] transition-[width,height] duration-300 ease-out group-hover:size-[22%] group-focus-visible:size-[22%]'
                    />
                </div>
            </div>
        </div>
    )
}

export const StickerSheet: FC = () => {
    const sheetRef = useRef<HTMLDivElement>(null)
    const [positions, setPositions] = useState<Array<Position>>(() => stickers.map(sticker => ({ x: sticker.x, y: sticker.y })))
    const [order, setOrder] = useState<Array<number>>(() => stickers.map((_, index) => index))

    const lift = (index: number) => {
        setOrder(current => [...current.filter(item => item !== index), index])
    }

    const move = (index: number, position: Position) => {
        setPositions(current => current.map((item, itemIndex) => (itemIndex === index ? position : item)))
    }

    const reset = () => {
        setPositions(stickers.map(sticker => ({ x: sticker.x, y: sticker.y })))
    }

    return (
        <section
            id='sticker-sheet'
            aria-labelledby='stickers-title'
            className='relative overflow-hidden py-20 md:py-28'
        >
            <div className='mx-auto max-w-[1400px] px-5 md:px-10'>
                <div className='grid gap-8 lg:grid-cols-12 lg:items-end'>
                    <div className='lg:col-span-7'>
                        <SectionLabel index='07'>Promises</SectionLabel>
                        <SplitText
                            as='h2'
                            id='stickers-title'
                            lines={['The Sticker', 'Sheet']}
                            italic='sticker'
                            className='mt-5 text-[clamp(3rem,7vw,6.5rem)] font-bold leading-[0.88] tracking-[-0.045em]'
                        />
                    </div>
                    <Reveal
                        delay={150}
                        className='lg:col-span-5'
                    >
                        <p className='text-lg leading-relaxed text-stone'>
                            Eight promises we make to every client, printed on a sheet you can rearrange. Peel one up, drag it somewhere better, make it yours.
                        </p>
                        <button
                            type='button'
                            onClick={reset}
                            className='mt-4 font-mono text-xs uppercase tracking-[0.14em] text-stone underline decoration-ember decoration-2 underline-offset-4 transition-colors hover:text-ink'
                        >
                            Put them back
                        </button>
                    </Reveal>
                </div>

                <div className='mt-12 grid gap-10 lg:grid-cols-12 lg:items-center'>
                    <Reveal
                        settle={-2}
                        y={40}
                        className='lg:col-span-8'
                    >
                        <div className='relative rounded-[1.75rem] border-2 border-dashed border-ink/15 bg-[#fbfaf7] p-3 shadow-[0_40px_80px_-50px_rgb(30_30_30/0.6)] md:p-5'>
                            <div className='flex items-center justify-between px-2 pb-3 font-mono text-[10px] uppercase tracking-[0.16em] text-stone'>
                                <span>Sheet no. 8 · Peel, stick, repeat</span>
                                <span className='hidden sm:inline'>Updraft promise co.</span>
                            </div>
                            <div
                                ref={sheetRef}
                                className='relative aspect-[4/5] w-full rounded-[1.25rem] bg-[radial-gradient(rgb(30_30_30/0.08)_1px,transparent_1.2px)] [background-size:14px_14px] sm:aspect-[16/10]'
                            >
                                {stickers.map((sticker, index) => (
                                    <StickerItem
                                        key={sticker.word}
                                        sticker={sticker}
                                        position={positions[index]}
                                        z={order.indexOf(index) + 1}
                                        onMove={position => move(index, position)}
                                        onLift={() => lift(index)}
                                        sheetRef={sheetRef}
                                    />
                                ))}
                            </div>
                        </div>
                    </Reveal>

                    <Reveal
                        delay={200}
                        className='lg:col-span-4'
                    >
                        <p className={cn(hand.className, 'rotate-[-3deg] text-3xl text-ember')}>the fine print, in plain words</p>
                        <dl className='mt-5 divide-y divide-ink/10 border-y border-ink/10'>
                            {stickers.map(sticker => (
                                <div
                                    key={sticker.word}
                                    className='flex items-baseline justify-between gap-4 py-2.5'
                                >
                                    <dt className='font-heading text-lg font-bold tracking-[-0.03em]'>{sticker.word}</dt>
                                    <dd className='text-right text-sm text-stone'>{sticker.line}</dd>
                                </div>
                            ))}
                        </dl>
                    </Reveal>
                </div>
            </div>
        </section>
    )
}
